import type { Deferred, Effect, Fiber } from 'effect'
import type {
  ConnectionState,
  Envelope,
  PushError,
  RealtimeTransport,
  RealtimeVisibility,
  Topic
} from './types'

export type PendingPush = {
  readonly topic: Topic
  readonly deferred: Deferred.Deferred<unknown, PushError>
}

export type SubscriptionId = number

export type SubscriptionHandler = (event: Envelope) => Effect.Effect<void>

type TopicJoinStall = {
  readonly episode: number
  readonly timer: Fiber.Fiber<void, never> | null
  readonly reported: boolean
}

export type TopicLifecycle =
  // No in-flight join and not currently joined.
  | { readonly _tag: 'idle'; readonly attempt: number }
  // Join sent, waiting for reply or timeout.
  | {
      readonly _tag: 'joining'
      readonly attempt: number
      readonly joinRef: string
      readonly ref: string
    }
  // Join acknowledged; normal event delivery state.
  | { readonly _tag: 'joined'; readonly joinRef: string; readonly ref: string }
  // Backoff window before next join attempt.
  | { readonly _tag: 'retryWait'; readonly attempt: number }

export type RuntimeModel = {
  connection: ConnectionState
  // Declarative desired connection state (true after connect(), false after disconnect()).
  desiredConnected: boolean
  // Browser visibility hint used to pause reconnect timers while hidden.
  visibility: RealtimeVisibility
  reconnectPausedByVisibility: boolean
  userInitiatedDisconnect: boolean
  // Generation fences stale transport callbacks/timers from previous connections.
  generation: number
  nextRef: number
  nextSubscriptionId: number
  reconnectAttempt: number
  transport: RealtimeTransport | null
  reconnectFiber: Fiber.Fiber<void, never> | null
  connectOpenTimeoutFiber: Fiber.Fiber<void, never> | null
  heartbeatFiber: Fiber.Fiber<void, never> | null
  heartbeatTimeoutFiber: Fiber.Fiber<void, never> | null
  pendingHeartbeatRef: string | null
  joinTimeoutFibers: Map<Topic, Fiber.Fiber<void, never>>
  joinRetryFibers: Map<Topic, Fiber.Fiber<void, never>>
  topicJoinStalls: Map<Topic, TopicJoinStall>
  nextTopicJoinStallEpisode: number
  desiredTopicRefCounts: Map<Topic, number>
  topicStates: Map<Topic, TopicLifecycle>
  handlersByTopic: Map<Topic, Map<SubscriptionId, SubscriptionHandler>>
  pendingJoinTopicByRef: Map<string, Topic>
  pendingPushesByRef: Map<string, PendingPush>
  joinRefByTopic: Map<Topic, string>
}

export function makeInitialModel(): RuntimeModel {
  return {
    connection: {
      _tag: 'disconnected',
      userInitiated: false
    },
    desiredConnected: false,
    visibility: 'visible',
    reconnectPausedByVisibility: false,
    userInitiatedDisconnect: false,
    generation: 0,
    nextRef: 1,
    nextSubscriptionId: 1,
    reconnectAttempt: 0,
    transport: null,
    reconnectFiber: null,
    connectOpenTimeoutFiber: null,
    heartbeatFiber: null,
    heartbeatTimeoutFiber: null,
    pendingHeartbeatRef: null,
    joinTimeoutFibers: new Map(),
    joinRetryFibers: new Map(),
    topicJoinStalls: new Map(),
    nextTopicJoinStallEpisode: 1,
    desiredTopicRefCounts: new Map(),
    topicStates: new Map(),
    handlersByTopic: new Map(),
    pendingJoinTopicByRef: new Map(),
    pendingPushesByRef: new Map(),
    joinRefByTopic: new Map()
  }
}

export function sortedJoinedTopics(model: RuntimeModel): ReadonlyArray<Topic> {
  const topics: Topic[] = []
  for (const [topic, state] of model.topicStates.entries()) {
    if (state._tag == 'joined') {
      topics.push(topic)
    }
  }

  return topics.sort((a, b) => a.localeCompare(b))
}

export function computeBackoffMs(opts: {
  attempt: number
  baseDelayMs: number
  maxDelayMs: number
  jitter: number
  random: number
}): number {
  const exponential = opts.baseDelayMs * Math.pow(2, Math.max(0, opts.attempt - 1))
  const capped = Math.min(exponential, opts.maxDelayMs)
  const jitterSpread = Math.max(0, opts.jitter)
  if (jitterSpread == 0) {
    return Math.round(capped)
  }

  const min = capped * (1 - jitterSpread)
  const max = capped * (1 + jitterSpread)
  const value = min + opts.random * (max - min)
  return Math.max(1, Math.round(value))
}
